import { Box, Stack } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IROVVideoCategory, IROVVideoMinimal, IROVVideosWithSharedCategories } from 'src/generated-types/rovvideo'
import { Flex } from 'src/styles/flexComponent'
import NoRovMovies from '../assets/svg/no-rov-movies.svg'
import { EmptyStatusCard } from './mooring/EmptyStatusCard'
import { ROVAccordion } from './ROVAccordion'
import ROVFolderContainer from './ROVFolderContainer'
import { ROVVideoComponent } from './ROVVideoComponent'

interface ROVContainerProps {
  rovVideosWithSharedCategories?: IROVVideosWithSharedCategories
  width: string
  customerId?: EntityId | null
  localityId?: EntityId | null
  mooringId?: EntityId | null
  positionId?: EntityId | null
  mainComponentId?: EntityId | null
  onCategoryChange?: (categoryId: EntityId | null) => void
  rowFlex?: string
  folderNameTruncateLength?: number
  height?: string | number
  isAccordionGroups?: boolean
  accordionBorder?: string
  minimalEmptyStatus?: boolean
  maxHeight?: string | number
  disabled?: boolean
}

const ROVContainer = ({
  rovVideosWithSharedCategories,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
  onCategoryChange,
  rowFlex,
  folderNameTruncateLength,
  height,
  isAccordionGroups = false,
  accordionBorder,
  minimalEmptyStatus = false,
  maxHeight,
  disabled = false,
}: ROVContainerProps) => {
  const { t } = useTranslation(['position'])
  const [selectedCategory, setSelectedCategory] = useState<EntityId | null>(null)

  const handleCategorySelect = (categoryId: EntityId | null) => {
    setSelectedCategory(categoryId)
    if (onCategoryChange) {
      onCategoryChange(categoryId)
    }
  }

  const { categoriesMap, videosWithoutCategory, allCategories, availableCategories } = useMemo(() => {
    const videosMap: Record<EntityId, { videos: IROVVideoMinimal[]; category: IROVVideoCategory }> = {}
    const uncategorizedVideos: IROVVideoMinimal[] = []
    const categoriesById = new Map<EntityId, IROVVideoCategory>()

    if (rovVideosWithSharedCategories?.sharedCategories) {
      rovVideosWithSharedCategories.sharedCategories.forEach((category: IROVVideoCategory) => {
        if (category.id) {
          categoriesById.set(category.id, category)
        }
      })
    }

    if (rovVideosWithSharedCategories?.videos) {
      rovVideosWithSharedCategories.videos.forEach((video: IROVVideoMinimal) => {
        if (video.category?.categoryName?.trim() && video.category.id) {
          const categoryId = video.category.id

          if (!videosMap[categoryId]) {
            videosMap[categoryId] = {
              videos: [],
              category: video.category,
            }
          }
          videosMap[categoryId].videos.push(video)
          categoriesById.set(categoryId, video.category)
        } else {
          uncategorizedVideos.push(video)
        }
      })
    }

    const sortedCategories = Object.entries(videosMap).sort(([, a], [, b]) =>
      a.category.categoryName.localeCompare(b.category.categoryName),
    )

    const allAvailableCategories = Array.from(categoriesById.values())

    return {
      categoriesMap: videosMap,
      videosWithoutCategory: uncategorizedVideos,
      allCategories: sortedCategories,
      availableCategories: allAvailableCategories,
    }
  }, [rovVideosWithSharedCategories])

  const shouldShowEmptyState =
    !rovVideosWithSharedCategories?.videos || rovVideosWithSharedCategories.videos.length === 0

  return shouldShowEmptyState ? (
    <Box height="100%">
      <EmptyStatusCard
        imageWidth="107px"
        image={NoRovMovies}
        header={t('rov-movies-empty-status-title', { ns: 'position' })}
        content={t('rov-movies-empty-status-content', { ns: 'position' })}
        headerFontSize="18px"
        contentFontSize="14px"
        contentClasses="mt-2"
        contentTopMargin="10px"
        imageContainerClasses="pb-4 pt-4"
        height="100%"
        hideImage={minimalEmptyStatus}
        compact={minimalEmptyStatus}
      />
    </Box>
  ) : isAccordionGroups ? (
    <Stack
      spacing={1}
      className="scrollbar"
      sx={{ overflowY: 'auto' }}
      height={height ?? '100%'}
      width={'100%'}
      padding={'8px'}
    >
      {allCategories.map(([, categoryData]) => (
        <ROVAccordion
          key={categoryData.category.id}
          categoryName={categoryData.category.categoryName}
          count={categoryData.videos.length}
          category={categoryData.category}
          customerId={customerId}
          localityId={localityId}
          mooringId={mooringId}
          positionId={positionId}
          mainComponentId={mainComponentId}
          rovVideosOfCategory={categoriesMap[categoryData.category.id].videos ?? []}
          availableCategories={availableCategories}
          rowFlex={rowFlex}
          folderNameTruncateLength={folderNameTruncateLength}
          border={accordionBorder}
          isShared={categoryData.category.isShared}
        />
      ))}

      <Flex.Row flexWrap={'wrap'} width={'100%'} gap={0.5} justifyContent={'space-between'}>
        {videosWithoutCategory.length > 0 &&
          videosWithoutCategory.map((video, index) => (
            <ROVVideoComponent
              key={video.id ?? index}
              rovvideo={video}
              width={'auto'}
              categories={availableCategories}
              isShowAction={true}
              customerId={customerId}
              localityId={localityId}
              mooringId={mooringId}
              positionId={positionId}
              mainComponentId={mainComponentId}
              rowFlex={rowFlex}
              isFolderContainer={false}
            />
          ))}
      </Flex.Row>
    </Stack>
  ) : (
    <ROVFolderContainer
      selectedCategory={selectedCategory}
      handleCategorySelect={handleCategorySelect}
      categoriesMap={categoriesMap}
      customerId={customerId}
      localityId={localityId}
      mooringId={mooringId}
      positionId={positionId}
      mainComponentId={mainComponentId}
      allCategories={allCategories}
      videosWithoutCategory={videosWithoutCategory}
      availableCategories={availableCategories}
      rowFlex={rowFlex}
      height={height}
      maxHeight={maxHeight}
      disabled={disabled}
    />
  )
}

export default ROVContainer
